import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo } from "react";
import { draftListQueryOptions } from "./useDraftListQuery";
import { useAllDrafts } from "./useDraftStore";

export interface MergedDraftItem {
  draft_id: string;
  title: string;
  updated_at: string;
  version: number;
}

function formatLocalDraftTitle(updatedAt: string): string {
  return `[${dayjs(updatedAt).format("YYYY-MM-DD")}] Daily Construction Report`;
}

export function useMergedDraftList(projectId: number): MergedDraftItem[] {
  const { data: serverDrafts } = useQuery(draftListQueryOptions(projectId));
  const localDrafts = useAllDrafts(projectId);

  return useMemo(() => {
    const serverResults = serverDrafts?.results ?? [];
    const serverDraftIds = new Set(serverResults.map((d) => d.draft_id));

    const serverItems: MergedDraftItem[] = serverResults.map((d) => ({
      draft_id: d.draft_id,
      title: d.title,
      updated_at: d.updated_at,
      version: d.version,
    }));

    const localOnlyItems: MergedDraftItem[] = (localDrafts ?? [])
      .filter((d) => !serverDraftIds.has(d.draft_id))
      .map((d) => ({
        draft_id: d.draft_id,
        title: formatLocalDraftTitle(d.updated_at),
        updated_at: d.updated_at,
        version: d.version,
      }));

    return [...serverItems, ...localOnlyItems].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }, [serverDrafts, localDrafts]);
}
